import React, { useEffect, useState } from "react";
import { useReveal } from "@/lib/useReveal";
import { Zap, Cpu, Trophy, Radio } from "lucide-react";

/** A tiny counter that animates from 0 → target once visible. */
function useCountUp(target, active, duration = 1400) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let raf, start;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      // easeOutQuad
      const eased = 1 - Math.pow(1 - p, 2);
      setN(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return n;
}

const stages = [
  {
    icon: Radio,
    label: "Fetch",
    body: "Sweep Reddit / forums / LinkedIn / YouTube with your buyer keywords in parallel.",
    accent: "text-lm-primary",
  },
  {
    icon: Cpu,
    label: "Analyze",
    body: "Every post scored for buying intent, pain, persona-fit, and relevance - in one AI pass.",
    accent: "text-lm-secondary",
  },
  {
    icon: Trophy,
    label: "Rank",
    body: "Top opportunities float up. Empty scans get refunded automatically - fair by default.",
    accent: "text-lm-primary",
  },
];

export default function ScanDemo() {
  const [headRef, headVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal(0.2);
  const fetched = useCountUp(187, gridVisible);
  const analyzed = useCountUp(163, gridVisible);
  const ranked = useCountUp(42, gridVisible);

  return (
    <section className="relative overflow-hidden lm-cv">
      <div className="absolute inset-0 pointer-events-none">
        <div className="lm-parallax lm-parallax-slow absolute top-8 right-10 w-72 h-72 rounded-full bg-lm-tint2/50 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-24">
        <div
          ref={headRef}
          className={`max-w-3xl mb-12 lm-reveal ${headVisible ? "is-visible" : ""}`}
        >
          <div className="text-xs font-mono uppercase tracking-widest text-lm-secondary mb-3">
            Under the hood
          </div>
          <h2 className="text-3xl md:text-5xl font-display font-semibold leading-tight tracking-tight">
            One scan. Three moves.<br />
            <span className="text-lm-text3">Zero background jobs.</span>
          </h2>
          <p className="mt-5 text-lg text-lm-text2 leading-relaxed">
            Everything runs on demand. Every scan is a single request. Nothing spins in the background quietly
            eating your budget.
          </p>
        </div>

        <div
          ref={gridRef}
          className={`grid grid-cols-1 lg:grid-cols-3 gap-5 lm-stagger`}
        >
          {stages.map((s, i) => (
            <div
              key={s.label}
              style={{ "--i": i }}
              className={`relative bg-lm-surface border border-lm-border rounded-lg p-6 md:p-7 lm-reveal ${gridVisible ? "is-visible" : ""}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-11 h-11 rounded-lg bg-lm-tint2 flex items-center justify-center ${s.accent}`}>
                  <s.icon className="w-5 h-5" strokeWidth={1.9} />
                </div>
                <div className="font-mono text-xs text-lm-text3">Step {i + 1}</div>
              </div>
              <div className="text-xl font-display font-semibold mb-2">{s.label}</div>
              <p className="text-sm text-lm-text2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>

        {/* Result banner */}
        <div
          className={`mt-8 rounded-lg bg-lm-primary text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 lm-reveal lm-reveal-scale ${gridVisible ? "is-visible" : ""}`}
          style={{ transitionDelay: "300ms" }}
        >
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-white/15 flex items-center justify-center">
              <Zap className="w-5 h-5" strokeWidth={2} />
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-white/60 mb-1">
                One scan · &lt; 60 seconds
              </div>
              <div className="text-lg font-display font-semibold">
                Real numbers from a real customer scan.
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 md:gap-10 text-center md:text-right">
            <Metric label="Fetched" value={fetched} />
            <Metric label="Analyzed" value={analyzed} />
            <Metric label="Ranked" value={ranked} />
          </div>
        </div>
      </div>
    </section>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-3xl md:text-4xl font-display font-semibold font-mono">{value}</div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/60">{label}</div>
    </div>
  );
}
